import { useEffect, type JSX } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { WORLD_THEME_IDS } from '@duck/contracts';
import { api } from '../../lib/api';
import { usePageMeta } from '../../lib/page-meta';
import { useAuthStore } from '../../state/auth.store';
import { OfficePage } from './OfficePage';

function isKnownTheme(value: string | undefined): boolean {
  return value !== undefined && (WORLD_THEME_IDS as readonly string[]).includes(value);
}

/**
 * RoomGate (ADR-0055) — the single entry point for a room deep-link `/{level_type}/{room_key}`. It makes
 * the URL the SOURCE OF TRUTH for which room you're in: it RESOLVES the key before OfficePage connects,
 * guarantees the invariant "my token's workspace === the URL's room", and only THEN mounts OfficePage
 * (which connects to that room and canonicalizes the address bar from there). Before this, OfficePage
 * always joined the account's own/home room and the canonicalizer overwrote the address bar — so a link
 * to someone else's room silently dropped you into your last session (the bug this fixes).
 *
 * Branches:
 *   • bad theme segment ............ → the marketing landing "/" (a two-segment path that isn't a room).
 *   • not signed in ................ → /login, returning here after auth (strict login wall, ТЗ §1).
 *   • stale / removed key (404) .... → /lobby (the room is gone).
 *   • already my token's room ...... → straight in (fast path — no join round-trip).
 *   • a DIFFERENT open room ........ → join it as my account, then in.
 *   • a DIFFERENT locked room ...... → /lobby with a password prompt prefilled for that room.
 *
 * OfficePage stays mounted across the id→slug canonicalize hop: react-query `keepPreviousData` holds the
 * resolved room while the new key refetches, so the same-room hop never remounts it (the PR #14 FPS bug).
 */
export function RoomGate(): JSX.Element {
  // A room deep-link is someone's private space — never for a search index.
  usePageMeta({ title: 'Your space — Duck Duck Space', noindex: true });
  const { levelType, roomKey } = useParams<{ levelType?: string; roomKey?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const authed = useAuthStore((s) => s.tokens !== null);
  const currentWs = useAuthStore((s) => s.user?.workspaceId ?? null);
  const setAuth = useAuthStore((s) => s.setAuth);

  // A two-segment URL whose theme segment isn't a real world theme is not a room link → front door.
  const badTheme = levelType !== undefined && !isKnownTheme(levelType);

  const resolved = useQuery({
    queryKey: ['resolve-room', roomKey],
    queryFn: () => api.resolveRoom(roomKey as string),
    enabled: authed && !badTheme && Boolean(roomKey),
    // Hold the previously-resolved room while a new key refetches, so the id→slug canonicalize hop (same
    // underlying workspace) keeps OfficePage mounted instead of flashing the splash / remounting the world.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
  });
  const target = resolved.data ?? null;
  // SWITCH decisions must act only on a FRESH resolution for the CURRENT key — never the placeholder of a
  // previously-resolved room (else, mid-navigation A→B, we'd read the room we're LEAVING and join/redirect
  // for it). `isPlaceholderData` is true exactly while `resolved.data` still holds the prior key's room.
  const freshTarget = resolved.isPlaceholderData ? null : target;
  const needSwitch = freshTarget !== null && currentWs !== null && freshTarget.workspaceId !== currentWs;

  const joinOpen = useMutation({
    mutationFn: (workspaceId: string) => api.joinSession({ workspaceId }),
    onSuccess: (result) => setAuth(result),
    // An OPEN room never rejects on the password; a transient failure lands in the lobby, not a hang.
    onError: () => navigate('/lobby', { replace: true, state: { roomGone: roomKey } }),
  });

  // Stale / removed link → back to the lobby (the room no longer exists).
  useEffect(() => {
    if (resolved.isError) navigate('/lobby', { replace: true, state: { roomGone: roomKey } });
  }, [resolved.isError, roomKey, navigate]);

  // Enter the URL's room: auto-join an OPEN one; bounce a LOCKED one to the lobby's password prompt. Gated
  // on the join not being in flight (`isPending`) rather than a permanent per-room ref — so it fires once
  // per switch AND re-fires when you later re-enter a room you'd previously left (no stuck "Finding…" hang).
  useEffect(() => {
    if (!needSwitch || freshTarget === null || joinOpen.isPending) return;
    if (freshTarget.hasPassword) {
      navigate('/lobby', { replace: true, state: { joinRoom: freshTarget } });
    } else {
      joinOpen.mutate(freshTarget.workspaceId);
    }
  }, [needSwitch, freshTarget, joinOpen, navigate]);

  if (badTheme) return <Navigate to="/" replace />;
  if (!authed) return <Navigate to="/login" replace state={{ from: location }} />;

  // Mount OfficePage once the token is scoped to the URL's room. Uses `target` (placeholder-inclusive) so
  // the id→slug canonicalize hop of the SAME workspace keeps it mounted (never remounts the world). On a
  // genuine A→B switch, `target` becomes the fresh B (≠ currentWs A) → splash until the join lands, so the
  // world shown never lags behind the URL beyond the brief placeholder window.
  const ready = target !== null && currentWs === target.workspaceId;
  if (ready) return <OfficePage />;
  return <RoomGateSplash />;
}

/** A warm "on our way in" splash while RoomGate resolves the link / joins the room. */
function RoomGateSplash(): JSX.Element {
  return (
    <div className="flex h-full w-full items-center justify-center bg-slate-950 text-white/70">
      <div className="flex flex-col items-center gap-3" role="status" aria-live="polite">
        <div className="animate-bounce text-4xl" aria-hidden>
          🦆
        </div>
        <p className="text-sm">Finding your room…</p>
      </div>
    </div>
  );
}
